<script setup lang="ts">
  import { ref, shallowRef, computed, onMounted, nextTick } from "vue";
  import { useRoute } from "vue-router";
  import { useOfferteBundels } from "../composables/useOfferteBundels";
  import { useDocumentSubmissions } from "../composables/useDocumentSubmissions";
  import { useAuthStore } from "../stores/auth";
  import { useToast } from "../composables/useToast";
  import { supabase } from "../lib/supabase";
  import { formatPercentage } from "../utils/formatUtils";
  import { getLatestStatus, getStatusColor, getStatusLabel } from "../utils/offerteStatus";
  import { sanitizeFileName, triggerFileDownload, validateUploadedFile } from "../utils/fileUtils";
  import AppHeader from "../components/AppHeader.vue";
  import CustomTag from "../components/CustomTag.vue";
  import SignaturePad from "../components/SignaturePad.vue";
  import OfferteDetailPanel from "../components/OfferteDetailPanel.vue";
  import NotitiesSection from "../components/NotitiesSection.vue";
  import Checkbox from "primevue/checkbox";
  import Button from "primevue/button";
  import SelectButton from "primevue/selectbutton";
  import AppIcon from "@/components/AppIcon.vue";

  const route = useRoute();
  const authStore = useAuthStore();
  const toast = useToast();

  const opdrachtgeverId = authStore.user?.id || "";
  const { loading, error, getBundelById, signBundel, markBundelBekeken } = useOfferteBundels(opdrachtgeverId);

  const bundel = ref<any>(null);
  const signature = ref<string | undefined>(undefined);
  const allTermsAccepted = ref(false);
  const acceptanceError = ref("");
  const signingMethod = ref<"direct" | "upload">("direct");
  const signingMethodOptions: Array<{ label: string; value: "direct" | "upload" }> = [
    { label: "Direct ondertekenen", value: "direct" },
    { label: "Handmatig PDF uploaden", value: "upload" },
  ];
  const uploadInput = ref<HTMLInputElement | null>(null);
  const uploadingManualPdf = ref(false);
  const submissionState = shallowRef<ReturnType<typeof useDocumentSubmissions> | null>(null);
  const isUploading = ref(false); // Track upload state

  // Detail panel state
  const selectedOfferteId = ref<string | null>(null);

  const bundelId = computed(() => {
    const id = route.params.id;
    return Array.isArray(id) ? id[0] : id;
  });

  const latestStatus = computed(() => getLatestStatus(bundel.value?.status || []));

  const isPending = computed(() => latestStatus.value !== "alles_geaccepteerd");

  const isSigned = computed(() => latestStatus.value === "alles_geaccepteerd");

  const isPartiallyAccepted = computed(() => latestStatus.value === "deels_geaccepteerd");

  const hasWrongSubmission = computed(() => latestStatus.value === "foute_inzending");

  const statusColor = computed(() => getStatusColor(latestStatus.value));
  const statusLabel = computed(() => getStatusLabel(latestStatus.value));

  const latestSubmission = computed(() => submissionState.value?.latestSubmission.value ?? null);
  const currentDocument = computed(() => submissionState.value?.currentDocument.value ?? null);
  const manualSubmissionLabel = computed(() => submissionState.value?.getSubmissionLabel(latestSubmission.value) || "");
  const submissionTitle = computed(
    () => submissionState.value?.getSubmissionTitle(latestSubmission.value?.submission_type) || "Getekende PDF",
  );
  const requiresRedownload = computed(() => submissionState.value?.requiresRedownload.value ?? false);
  const canUploadManualPdf = computed(() => Boolean(submissionState.value?.canUpload.value && !isSigned.value));

  const bundelSubtotaal = computed(() => {
    if (!bundel.value?.offertes?.length) return 0;
    return bundel.value.offertes.reduce((sum: number, o: any) => {
      return sum + (o.totaalIncKorting || 0);
    }, 0);
  });

  const hasBundelKorting = computed(() => {
    return !!(bundel.value?.korting_percentage || bundel.value?.korting_bedrag);
  });

  const bundelKortingBedrag = computed(() => {
    if (!hasBundelKorting.value) return 0;
    if (bundel.value?.korting_bedrag) return bundel.value.korting_bedrag;
    if (bundel.value?.korting_percentage) {
      return bundelSubtotaal.value * (bundel.value.korting_percentage / 100);
    }
    return 0;
  });

  const bundelTotal = computed(() => {
    return bundelSubtotaal.value - bundelKortingBedrag.value;
  });

  const isPanelOpen = computed(() => selectedOfferteId.value !== null);

  const selectedOfferte = computed(() => {
    if (!selectedOfferteId.value || !bundel.value?.offertes) return null;
    return bundel.value.offertes.find((o: any) => o.id === selectedOfferteId.value) || null;
  });

  const selectedOfferteIndex = computed(() => {
    if (!selectedOfferteId.value || !bundel.value?.offertes) return -1;
    return bundel.value.offertes.findIndex((o: any) => o.id === selectedOfferteId.value);
  });

  function selectOfferte(offerteId: string) {
    if (selectedOfferteId.value === offerteId) {
      selectedOfferteId.value = null;
    } else {
      selectedOfferteId.value = offerteId;
    }
  }

  function closePanel() {
    selectedOfferteId.value = null;
  }

  function selectPrev() {
    if (!bundel.value?.offertes || selectedOfferteIndex.value <= 0) return;
    const prevOfferte = bundel.value.offertes[selectedOfferteIndex.value - 1];
    selectedOfferteId.value = prevOfferte.id;
    scrollSelectedIntoView();
  }

  function selectNext() {
    if (!bundel.value?.offertes || selectedOfferteIndex.value >= bundel.value.offertes.length - 1) return;
    const nextOfferte = bundel.value.offertes[selectedOfferteIndex.value + 1];
    selectedOfferteId.value = nextOfferte.id;
    scrollSelectedIntoView();
  }

  function scrollSelectedIntoView() {
    nextTick(() => {
      const el = document.querySelector(".offerte-card.selected");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    });
  }

  function formatCurrency(amount: number | null | undefined): string {
    return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(amount || 0);
  }

  function formatDate(date: string | null): string {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("nl-NL");
  }

  const setAcceptanceError = (err: string) => {
    acceptanceError.value = err;
  };

  function openUploadPicker() {
    if (!canUploadManualPdf.value || uploadingManualPdf.value) return;
    uploadInput.value?.click();
  }

  async function downloadCurrentPdf() {
    if (!submissionState.value) return;

    try {
      const bundelNr = bundel.value?.bundel_nr;
      const fileName = bundelNr ? `${sanitizeFileName(bundelNr)}.pdf` : undefined;
      const signedUrl = await submissionState.value.getPdfSignedUrl(fileName);
      if (!signedUrl) {
        throw new Error("PDF nog niet beschikbaar");
      }
      if (fileName) {
        triggerFileDownload(signedUrl, fileName);
      } else {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Error downloading current PDF:", err);
      toast.error(err instanceof Error ? err.message : "Kon PDF niet downloaden", err);
    }
  }

  async function openUploadedPdf() {
    const filePath = latestSubmission.value?.uploaded_file_path;
    if (!filePath) return;

    try {
      const bundelNr = bundel.value?.bundel_nr;
      const fileName = bundelNr ? `${sanitizeFileName(bundelNr)}-getekend.pdf` : undefined;
      const { data, error: signedUrlError } = await supabase.storage
        .from("userFiles")
        .createSignedUrl(filePath, 3600, fileName ? { download: fileName } : undefined);
      if (signedUrlError) throw signedUrlError;
      if (data?.signedUrl) {
        if (fileName) {
          triggerFileDownload(data.signedUrl, fileName);
        } else {
          window.open(data.signedUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (err) {
      console.error("Error opening uploaded PDF:", err);
      toast.error(err instanceof Error ? err.message : "Kon geuploade PDF niet openen", err);
    }
  }

  async function handleManualPdfUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file || !bundel.value || !submissionState.value) return;

    uploadingManualPdf.value = true;

    try {
      if (requiresRedownload.value) {
        throw new Error("Er is een nieuwer document beschikbaar. Download eerst de laatste PDF.");
      }

      // Validate size + type and derive a SAFE extension/contentType from the
      // allow-list — never trust file.name (path injection) or file.type
      // (spoofed Content-Type on the public bucket) verbatim.
      const validation = validateUploadedFile(file);
      if (!validation.ok) {
        toast.error(validation.error, undefined, { report: false });
        return;
      }

      // UUID-prefixed path so a crafted file.name can never inject `../` segments.
      const filePath = `bundels/${bundel.value.id}/manual-signed-${crypto.randomUUID()}.${validation.value.extension}`;

      const { error: uploadError } = await supabase.storage.from("userFiles").upload(filePath, file, {
        upsert: false,
        contentType: validation.value.contentType,
      });

      if (uploadError) throw uploadError;

      await submissionState.value.createSubmission(filePath, file.name, validation.value.contentType);
      bundel.value = await getBundelById(bundel.value.id);
      await submissionState.value.load();
      toast.success("Getekende PDF geupload. Het inspectiebureau controleert uw inzending.");
    } catch (err) {
      console.error("Error uploading manual PDF:", err);
      toast.error(err instanceof Error ? err.message : "Kon getekende PDF niet uploaden", err);
    } finally {
      target.value = "";
      uploadingManualPdf.value = false;
    }
  }

  async function handleSign(signatureData: string) {
    if (!bundel.value) return;

    try {
      isUploading.value = true; // Start loading state

      acceptanceError.value = "";

      // Validate signature data
      if (!signatureData || signatureData.length === 0) {
        throw new Error("Signature validation failed - no data provided");
      }

      // Convert signature to blob
      const signatureBlob = await fetch(signatureData).then((res) => {
        if (!res.ok) {
          throw new Error("Failed to create signature blob");
        }
        return res.blob();
      });

      const timestamp = new Date().getTime();
      const filePath = `bundels/${bundel.value.id}/handtekening.png`;

      // Upload signature to storage
      const { error: uploadError } = await supabase.storage.from("userFiles").upload(filePath, signatureBlob, {
        contentType: "image/png",
        upsert: true,
      });

      // Handle storage upload errors with specific messaging
      if (uploadError) {
        console.error("Storage upload error:", uploadError);

        // Handle RLS policy errors
        if (uploadError.message?.includes("row-level security")) {
          throw new Error("U bent niet geautoriseerd om deze bundel te tekenen. Neem contact op met het bedrijf.");
        }

        if (uploadError.message?.includes("400") || uploadError.message?.includes("Bad Request")) {
          throw new Error(
            "Er is een probleem met de handtekening. Controleer dat u een geldige handtekening heeft gemaakt en probeer opnieuw.",
          );
        }

        throw new Error(`Fout bij uploaden handtekening: ${uploadError.message}`);
      }

      // Get public URL for signature
      const {
        data: { publicUrl },
      } = supabase.storage.from("userFiles").getPublicUrl(filePath);

      const urlWithCacheBuster = `${publicUrl}?t=${timestamp}`;

      // Sign the bundel in the database. Setting the handtekening triggers
      // server-side regeneration of the bundel PDF
      // (trg_offerte_bundels_regenerate_document_on_handtekening).
      const success = await signBundel(bundel.value.id, urlWithCacheBuster);
      if (success) {
        bundel.value = await getBundelById(bundel.value.id);
        toast.success("Bundel succesvol getekend");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Er is een onbekende fout opgetreden bij het tekenen van de bundel";

      console.error("Error signing bundel:", err);
      toast.error(errorMessage, err);
      acceptanceError.value = errorMessage;
    } finally {
      isUploading.value = false; // End loading state
    }
  }

  onMounted(async () => {
    try {
      bundel.value = await getBundelById(bundelId.value);
      if (!bundel.value) {
        error.value = "Bundel niet gevonden";
      } else {
        // Track that the opdrachtgever opened this bundel (cascades to member offertes).
        void markBundelBekeken(bundel.value.id);
        const state = useDocumentSubmissions("offerte_bundel", bundel.value.id);
        submissionState.value = state;
        await state.load();
      }
    } catch (err) {
      console.error("Error loading bundel:", err);
      error.value = "Er is een fout opgetreden bij het laden van de bundel";
    }
  });
</script>

<template>
  <div>
    <AppHeader
      :title="bundel ? `${bundel.bundel_nr} — ${bundel.naam || 'Bundel'}` : 'Bundel'"
      :show-back-button="true"
      back-to="/offertes?tab=1"
      back-text="Alle bundels"
    />

    <div class="mx-auto max-w-7xl py-8 pb-28 sm:px-2 lg:px-3">
      <!-- Loading -->
      <div v-if="loading && !bundel" class="flex min-h-64 items-center justify-center">
        <div class="text-center">
          <div class="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p>Bundel laden...</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex min-h-64 items-center justify-center">
        <div class="text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <i class="pi pi-exclamation-triangle text-2xl text-red-600"></i>
          </div>
          <p class="font-medium text-red-600">{{ error }}</p>
        </div>
      </div>

      <!-- Content -->
      <template v-else-if="bundel">
        <!-- Bundel Header -->
        <div class="bundel-header">
          <div class="bundel-header-info">
            <div class="flex flex-wrap items-center gap-3">
              <h2 class="text-background-900 text-xl font-semibold">{{ bundel.naam || "Bundel" }}</h2>
              <span class="text-background-500 font-mono text-sm">{{ bundel.bundel_nr }}</span>
              <CustomTag :color="statusColor" :label="statusLabel" />
            </div>
            <p v-if="bundel.omschrijving" class="text-background-600 mt-1 text-sm">
              {{ bundel.omschrijving }}
            </p>
          </div>
          <div class="bundel-header-total">
            <template v-if="hasBundelKorting">
              <span class="text-background-500 text-xs font-medium tracking-wide uppercase">Subtotaal</span>
              <span class="text-background-500 font-mono text-base line-through">{{
                formatCurrency(bundelSubtotaal)
              }}</span>
              <span class="text-xs font-medium text-emerald-600">
                -{{ formatCurrency(bundelKortingBedrag) }}
                <template v-if="bundel.korting_percentage"
                  >({{ formatPercentage(bundel.korting_percentage) }})</template
                >
              </span>
              <span class="text-background-900 font-mono text-2xl font-semibold">{{
                formatCurrency(bundelTotal)
              }}</span>
            </template>
            <template v-else>
              <span class="text-background-500 text-xs font-medium tracking-wide uppercase">Totaal</span>
              <span class="text-background-900 font-mono text-2xl font-semibold">{{
                formatCurrency(bundelTotal)
              }}</span>
            </template>
            <span class="text-background-500 text-xs">{{ bundel.offertes?.length || 0 }} offertes</span>
          </div>
        </div>

        <!-- Signed info -->
        <div v-if="isSigned" class="mt-4 rounded-sm border border-green-200 bg-green-50 p-4">
          <div class="flex items-center gap-3">
            <i class="pi pi-check-circle text-xl text-green-600"></i>
            <div>
              <p class="text-sm font-medium text-green-800">Deze bundel is getekend</p>
              <p class="text-sm text-green-700">
                Getekend op {{ formatDate(bundel.accept_datum) }}
                <template v-if="bundel.getekend_door_opdrachtgever?.naam || bundel.getekend_door">
                  door {{ bundel.getekend_door_opdrachtgever?.naam || bundel.getekend_door }}
                </template>
              </p>
            </div>
          </div>
          <div v-if="bundel.handtekening" class="mt-3">
            <img
              :src="bundel.handtekening"
              alt="Handtekening"
              class="h-20 rounded-sm border border-green-200 bg-white object-contain"
            />
          </div>
        </div>

        <!-- Split View: List + Detail Panel -->
        <div class="mt-6">
          <h3 class="text-background-800 mb-4 text-base font-semibold">
            Offertes in deze bundel ({{ bundel.offertes?.length || 0 }})
          </h3>

          <div class="split-view" :class="{ 'panel-open': isPanelOpen }">
            <!-- Offerte List -->
            <div class="offerte-list">
              <div class="sticky top-2 space-y-2">
                <div
                  v-for="offerte in bundel.offertes"
                  :key="offerte.id"
                  class="offerte-card"
                  :class="{ selected: selectedOfferteId === offerte.id }"
                  @click="selectOfferte(offerte.id)"
                >
                  <div class="offerte-card-content">
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="text-background-900 text-sm font-semibold">
                          {{ offerte.locatie?.naam || "Onbekende locatie" }}</span
                        >
                      </div>
                      <p class="text-background-600 mt-0.5 truncate text-sm">{{ offerte.offerteNr || "Offerte" }}</p>
                    </div>
                    <div class="shrink-0 text-right">
                      <span class="text-background-900 font-mono text-sm font-semibold">
                        {{ formatCurrency(offerte.totaalIncKorting || 0) }}
                      </span>
                    </div>
                  </div>
                  <!-- CSS triangle connector -->
                  <div v-if="selectedOfferteId === offerte.id && isPanelOpen" class="card-connector"></div>
                </div>
              </div>
            </div>

            <!-- Detail Panel -->
            <div v-if="isPanelOpen && selectedOfferte" class="detail-panel-wrapper">
              <OfferteDetailPanel
                :offerte="selectedOfferte"
                :current-index="selectedOfferteIndex"
                :total-count="bundel.offertes?.length || 0"
                @close="closePanel"
                @prev="selectPrev"
                @next="selectNext"
              />
            </div>
          </div>
        </div>

        <!-- Signing Area (pending only, not when a PDF upload is awaiting review) -->
        <div v-if="isPending && !isPartiallyAccepted" class="signing-area">
          <div class="signing-header">
            <h3 class="text-background-900 text-lg font-semibold">Bundel ondertekenen</h3>
            <p class="text-background-600 mt-1 text-sm">
              Door deze bundel te tekenen gaat u akkoord met alle {{ bundel.offertes?.length || 0 }} offertes en
              bijbehorende voorwaarden.
            </p>
          </div>

          <div class="signing-summary">
            <div class="flex items-center justify-between">
              <span class="text-background-700 text-sm font-medium">{{ bundel.offertes?.length || 0 }} offertes</span>
              <span class="text-background-900 font-mono text-sm line-through">{{
                formatCurrency(bundelSubtotaal)
              }}</span>
            </div>
            <template v-if="hasBundelKorting">
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-emerald-700">
                  Bundelkorting
                  <template v-if="bundel.korting_percentage"
                    >({{ formatPercentage(bundel.korting_percentage) }})</template
                  >
                </span>
                <span class="font-mono text-sm font-medium text-emerald-700">
                  - {{ formatCurrency(bundelKortingBedrag) }}
                </span>
              </div>
              <div class="border-background-200 mt-1 flex items-center justify-between border-t pt-1">
                <span class="text-background-900 text-sm font-semibold">Totaal</span>
                <span class="text-background-900 font-mono text-lg font-semibold">{{
                  formatCurrency(bundelTotal)
                }}</span>
              </div>
            </template>
            <template v-else>
              <div class="border-background-200 mt-1 flex items-center justify-between border-t pt-1">
                <span class="text-background-900 text-sm font-semibold">Totaal</span>
                <span class="text-background-900 font-mono text-lg font-semibold">{{
                  formatCurrency(bundelTotal)
                }}</span>
              </div>
            </template>
          </div>

          <div class="border-background-200 bg-background-50 rounded-sm border p-3">
            <h4 class="text-background-700 mb-3 text-lg font-bold">Kies hoe u wilt ondertekenen</h4>
            <SelectButton
              v-model="signingMethod"
              :options="signingMethodOptions"
              optionLabel="label"
              optionValue="value"
              :allowEmpty="false"
            />
          </div>

          <div class="mt-6 flex justify-end">
            <Button label="Download PDF" severity="contrast" @click="downloadCurrentPdf">
              <template #icon><AppIcon name="download" /></template>
            </Button>
          </div>

          <div v-if="signingMethod === 'direct'" class="border-background-200 bg-background-50 rounded-sm border p-3">
            <div class="border-background-200 bg-background-50 flex items-start gap-3 pb-3">
              <Checkbox v-model="allTermsAccepted" inputId="bundel-terms" binary required />
              <label for="bundel-terms" class="text-background-700 cursor-pointer text-sm">
                Ik accepteer de algemene voorwaarden en inspectieplannen voor alle offertes in deze bundel.
              </label>
            </div>
            <h4 class="text-background-700 mb-2 text-sm font-medium">Uw handtekening</h4>
            <SignaturePad
              v-model="signature"
              :save-disabled="!allTermsAccepted"
              save-button-text="Tekenen & akkoord"
              clear-pad-text="Wissen"
              :can-edit="true"
              @save="handleSign"
              @error="setAcceptanceError"
            />
            <p v-if="acceptanceError" class="mt-2 text-sm text-red-600">
              {{ acceptanceError }}
            </p>
          </div>

          <div v-else class="border-background-200 bg-background-50 space-y-3 rounded-sm border p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-background-700 text-sm font-medium">{{ submissionTitle }}</p>
                <p class="text-muted-color mt-1 text-sm">{{ manualSubmissionLabel || "Nog geen upload" }}</p>
                <p v-if="requiresRedownload" class="mt-1 text-sm text-amber-700">
                  Er is een nieuw document beschikbaar. Download eerst de laatste PDF voordat u opnieuw uploadt.
                </p>
                <p v-else-if="hasWrongSubmission" class="mt-1 text-sm text-red-700">
                  De vorige inzending is afgekeurd. Download de laatste PDF, onderteken opnieuw en upload opnieuw.
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  v-if="latestSubmission?.uploaded_file_path"
                  label="Bekijk upload"
                  severity="secondary"
                  @click="openUploadedPdf"
                />
                <Button
                  :label="latestSubmission?.uploaded_file_path ? 'Nieuwe PDF uploaden' : 'Upload getekende PDF'"
                  :loading="uploadingManualPdf"
                  :disabled="!canUploadManualPdf"
                  @click="openUploadPicker"
                />
              </div>
            </div>
            <input
              ref="uploadInput"
              class="hidden"
              type="file"
              accept="application/pdf"
              @change="handleManualPdfUpload"
            />
            <p v-if="currentDocument?.status !== 'completed'" class="text-sm text-slate-600">
              Er wordt een nieuw document voorbereid. Uploaden is beschikbaar zodra de PDF gereed is.
            </p>
          </div>
        </div>

        <NotitiesSection :entity-type="'offerte_bundel'" :entity-id="bundelId" class="mt-4" />
      </template>
    </div>
  </div>
</template>

<style scoped>
  .bundel-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--p-background-200);
  }

  .bundel-header-info {
    flex: 1;
    min-width: 0;
  }

  .bundel-header-total {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.15rem;
    flex-shrink: 0;
  }

  /* Split View Layout */
  .split-view {
    display: flex;
    gap: 0;
    position: relative;
  }

  .offerte-list {
    flex: 1;
    min-width: 0;
  }

  .split-view.panel-open .offerte-list {
    flex: 0 0 50%;
    max-width: 50%;
  }

  .detail-panel-wrapper {
    flex: 0 0 50%;
    max-width: 50%;
    margin-left: 0.5rem;
  }

  /* Slim Offerte Cards */
  .offerte-card {
    border: 1px solid var(--p-background-200);
    border-radius: 0.25rem;
    background: var(--p-background-0);
    cursor: pointer;
    transition:
      border-color 200ms ease,
      box-shadow 200ms ease;
    position: relative;
  }

  .offerte-card:hover {
    border-color: var(--p-primary-300);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .offerte-card.selected {
    border-color: var(--p-primary-500);
    border-left: 3px solid var(--p-primary-500);
    background: var(--p-background-50);
  }

  .offerte-card-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
  }

  /* CSS triangle connector pointing right toward the panel */
  .card-connector {
    position: absolute;
    right: -0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 0.5rem solid transparent;
    border-bottom: 0.5rem solid transparent;
    border-left: 0.5rem solid var(--p-primary-500);
    z-index: 1;
  }

  .signing-area {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid var(--p-background-200);
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .signing-summary {
    padding: 0.75rem 1rem;
    background: var(--p-background-50);
    border: 1px solid var(--p-background-200);
    border-radius: 0.25rem;
  }

  @media (max-width: 768px) {
    .split-view {
      flex-direction: column;
    }

    .split-view.panel-open .offerte-list {
      flex: 1;
      max-width: 100%;
    }

    .detail-panel-wrapper {
      flex: 1;
      max-width: 100%;
      margin-left: 0;
      margin-top: 1rem;
      height: auto;
    }

    .card-connector {
      display: none;
    }

    .bundel-header {
      flex-direction: column;
      gap: 1rem;
    }

    .bundel-header-total {
      align-items: flex-start;
    }
  }
</style>
